import {
  COLUMN_INDEX_MAP,
  CREATIVE_COLUMN_INDEX_MAP,
  RawCreativeHistoryRow,
  RawHistoryRow,
  UUID_REGEX,
} from "./constants";
import { getCreativeChangeDetails } from "./creativeCustomCases";
import {
  CAMPAIGN_FIELD_PATH_TO_NAME_DICT,
  CAMPAIGN_HISTORY_DICT_REGEX_PATH,
  CREATIVE_FIELD_PATH_TO_NAME_DICT,
  CREATIVE_HISTORY_DICT_REGEX_PATH,
  getLineitemFieldToNameDict,
  LINEITEM_HISTORY_DICT_REGEX_PATH,
} from "./fieldMappings";
import {
  ChangeHistoryAction,
  ChangeHistoryChange,
  ChangeHistoryRow,
} from "./types";

export const MISSING_ID = "Missing ID";

export const getFieldNameCount = (
  data: ChangeHistoryRow[],
  fieldName: string
): number => {
  return data.filter((row) =>
    row.changes.some((change) => change.fieldName === fieldName)
  ).length;
};

export const getEntityIdCount = (
  data: ChangeHistoryRow[],
  entityId: string
): number => {
  return data.filter((row) =>
    row.changes.some((change) => change.entityId === entityId)
  ).length;
};

export const getChangedByCount = (
  data: ChangeHistoryRow[],
  changedBy: string
): number => {
  return data.filter((row) => row.changedBy === changedBy).length;
};

export const getFieldNameByPath = (path: string): string => {
  const lineitemIndexMatch = path.match(/\/lineitems\/(\d+)\//);
  const lineitemIndex = lineitemIndexMatch
    ? parseInt(lineitemIndexMatch[1], 10)
    : 0;

  const LINEITEM_FIELD_PATH_TO_NAME_DICT =
    getLineitemFieldToNameDict(lineitemIndex);

  let field = LINEITEM_FIELD_PATH_TO_NAME_DICT[path];

  if (!field) {
    const pathArray = path?.split("/");
    pathArray?.pop();
    field = LINEITEM_FIELD_PATH_TO_NAME_DICT[pathArray.join("/")];
  }

  if (!field) {
    const fieldName = LINEITEM_HISTORY_DICT_REGEX_PATH.find((reg) =>
      reg.regex.test(path)
    );
    if (fieldName) {
      let fieldDetail =
        fieldName.hasDetails && path.match(fieldName.regex)?.[1];
      fieldDetail = fieldDetail ? ` (${fieldDetail})` : "";
      field = fieldName?.display + fieldDetail;
    }
  }

  if (field) {
    return field;
  }

  const parsedAsArrayElem = path.replace(/\[.*?\]/g, "");
  if (LINEITEM_FIELD_PATH_TO_NAME_DICT[parsedAsArrayElem]) {
    return `one of ${LINEITEM_FIELD_PATH_TO_NAME_DICT[parsedAsArrayElem]}`;
  }

  const pathArray = path?.split("/");
  pathArray?.pop();

  field =
    CAMPAIGN_FIELD_PATH_TO_NAME_DICT[path] ||
    CAMPAIGN_FIELD_PATH_TO_NAME_DICT[pathArray.join("/")];

  if (!field) {
    const fieldName = CAMPAIGN_HISTORY_DICT_REGEX_PATH.find((reg) =>
      reg.regex.test(path)
    );
    if (fieldName) {
      field = fieldName?.display;
    }
  }

  if (field) {
    return field;
  }

  return path;
};

export const getCreativeFieldNameByPath = (path: string): string => {
  let field = CREATIVE_FIELD_PATH_TO_NAME_DICT[path];

  if (!field) {
    const pathArray = path?.split("/");
    pathArray?.pop();
    field = CREATIVE_FIELD_PATH_TO_NAME_DICT[pathArray.join("/")];
  }

  if (!field) {
    const fieldName = CREATIVE_HISTORY_DICT_REGEX_PATH.find((reg) =>
      reg.regex.test(path)
    );
    if (fieldName) {
      let fieldDetail =
        fieldName.hasDetails && path.match(fieldName.regex)?.[1];
      fieldDetail = fieldDetail ? ` (${fieldDetail})` : "";
      field = fieldName?.display + fieldDetail;
    }
  }

  return field || path;
};

export const isUUID = (id: string): boolean => {
  return UUID_REGEX.test(id);
};

/**
 * TabSeparated comes back with the newlines inside vast/markup unescaped, so those
 * records get shredded by a plain split. Reading JSON instead keeps them in one piece.
 */
export const parseJSONRows = (
  payload: { data?: Record<string, string>[] } | null,
  columns: readonly string[]
): string[][] =>
  (payload?.data ?? []).map((row) => columns.map((column) => row[column] ?? ""));

export const parseTSV = (tsvText: string): string[][] => {
  const rows = tsvText.trim().split("\n");
  return rows
    .map((row) => row.split("\t"))
    .filter((row) => row.length > 1 && row[0]);
};

export const mapChangeType = (changeType: string): ChangeHistoryAction => {
  const normalized = changeType.toLowerCase();
  if (normalized === "updated" || normalized === "update") return "updated";
  if (normalized === "added" || normalized === "add") return "added";
  if (
    normalized === "removed" ||
    normalized === "remove" ||
    normalized === "deleted" ||
    normalized === "delete"
  )
    return "removed";
  return "updated";
};

const parseArrayField = (field: string): string[] => {
  if (!field || field === "[]") return [];
  try {
    const jsonString = field.replace(/'/g, '"');
    return JSON.parse(jsonString);
  } catch {
    // Handle unquoted values like [tag1, tag2]
    const stripped = field.replace(/^\[|]$/g, "").trim();
    if (stripped) {
      return stripped
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [];
  }
};

export const transformTSVToRows = (
  parsedData: string[][]
): ChangeHistoryRow[] => {
  const rawRows: RawHistoryRow[] = parsedData.map((row) => ({
    datetime: row[COLUMN_INDEX_MAP.DATETIME] || "",
    campaignId: row[COLUMN_INDEX_MAP.CAMPAIGN_ID] || "",
    lineitemId: row[COLUMN_INDEX_MAP.LINEITEM_ID] || "",
    fieldPath: row[COLUMN_INDEX_MAP.FIELD_PATH] || "",
    changeType: row[COLUMN_INDEX_MAP.CHANGE_TYPE] || "",
    previousValue: row[COLUMN_INDEX_MAP.PREVIOUS_VALUE] || "",
    newValue: row[COLUMN_INDEX_MAP.NEW_VALUE] || "",
    user: row[COLUMN_INDEX_MAP.USER] || "",
    arrayAdded: row[COLUMN_INDEX_MAP.ARRAY_ADDED] || "[]",
    arrayRemoved: row[COLUMN_INDEX_MAP.ARRAY_REMOVED] || "[]",
  }));

  const groupedByTimestampAndUser = new Map<string, RawHistoryRow[]>();

  rawRows.forEach((row) => {
    const isLineitemPath = row.fieldPath.includes("/lineitems/");
    const entityId = isLineitemPath ? row.lineitemId : row.campaignId;
    const key = `${row.datetime}_${row.user}_${entityId}`;
    if (!groupedByTimestampAndUser.has(key)) {
      groupedByTimestampAndUser.set(key, []);
    }
    groupedByTimestampAndUser.get(key)!.push(row);
  });

  const historyRows: ChangeHistoryRow[] = [];
  let rowIdCounter = 1;

  groupedByTimestampAndUser.forEach((rows) => {
    if (rows.length === 0) return;

    const firstRow = rows[0];
    const changes: ChangeHistoryChange[] = [];
    let changeIdCounter = 1;

    rows.forEach((row) => {
      const isLineitemPath = row.fieldPath.includes("/lineitems/");
      const entityId = isLineitemPath ? row.lineitemId : row.campaignId;
      const fieldName = getFieldNameByPath(row.fieldPath);
      const entityType = isLineitemPath ? "Lineitem" : "Campaign";

      const hasValues = row.previousValue || row.newValue;

      if (!hasValues) {
        const arrayAdded = parseArrayField(row.arrayAdded);
        const arrayRemoved = parseArrayField(row.arrayRemoved);

        if (arrayAdded.length > 0) {
          changes.push({
            id: `${rowIdCounter}-${changeIdCounter++}`,
            action: "added",
            fieldName,
            fieldPath: row.fieldPath,
            newValue: JSON.stringify(arrayAdded),
            entityType,
            entityId: entityId || MISSING_ID,
            arrayLength: arrayAdded.length,
          });
        }

        if (arrayRemoved.length > 0) {
          changes.push({
            id: `${rowIdCounter}-${changeIdCounter++}`,
            action: "removed",
            fieldName,
            fieldPath: row.fieldPath,
            newValue: JSON.stringify(arrayRemoved),
            entityType,
            entityId: entityId || MISSING_ID,
            arrayLength: arrayRemoved.length,
          });
        }
      } else {
        changes.push({
          id: `${rowIdCounter}-${changeIdCounter++}`,
          action: mapChangeType(row.changeType),
          fieldName,
          fieldPath: row.fieldPath,
          oldValue: row.previousValue || undefined,
          newValue: row.newValue || undefined,
          entityType,
          entityId: entityId || MISSING_ID,
        });
      }
    });

    if (changes.length > 0) {
      historyRows.push({
        id: String(rowIdCounter),
        timestamp: new Date(firstRow.datetime),
        changes,
        changedBy: firstRow.user,
      });

      rowIdCounter++;
    }
  });

  return historyRows.sort(
    (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
  );
};

export const transformCreativeTSVToRows = (
  parsedData: string[][]
): ChangeHistoryRow[] => {
  const rawRows: RawCreativeHistoryRow[] = parsedData.map((row) => ({
    datetime: row[CREATIVE_COLUMN_INDEX_MAP.DATETIME] || "",
    creativeId: row[CREATIVE_COLUMN_INDEX_MAP.CREATIVE_ID] || "",
    oldSnapshotId: row[CREATIVE_COLUMN_INDEX_MAP.OLD_SNAPSHOT_ID] || "",
    newSnapshotId: row[CREATIVE_COLUMN_INDEX_MAP.NEW_SNAPSHOT_ID] || "",
    fieldPath: row[CREATIVE_COLUMN_INDEX_MAP.FIELD_PATH] || "",
    changeType: row[CREATIVE_COLUMN_INDEX_MAP.CHANGE_TYPE] || "",
    previousValue: row[CREATIVE_COLUMN_INDEX_MAP.PREVIOUS_VALUE] || "",
    newValue: row[CREATIVE_COLUMN_INDEX_MAP.NEW_VALUE] || "",
    user: row[CREATIVE_COLUMN_INDEX_MAP.USER] || "",
    arrayAdded: row[CREATIVE_COLUMN_INDEX_MAP.ARRAY_ADDED] || "[]",
    arrayRemoved: row[CREATIVE_COLUMN_INDEX_MAP.ARRAY_REMOVED] || "[]",
  }));

  const groupedByTimestampAndUser = new Map<string, RawCreativeHistoryRow[]>();

  rawRows.forEach((row) => {
    const key = `${row.datetime}_${row.user}_${row.creativeId}`;
    if (!groupedByTimestampAndUser.has(key)) {
      groupedByTimestampAndUser.set(key, []);
    }
    groupedByTimestampAndUser.get(key)!.push(row);
  });

  const historyRows: ChangeHistoryRow[] = [];
  let rowIdCounter = 1;

  groupedByTimestampAndUser.forEach((rows) => {
    if (rows.length === 0) return;

    const firstRow = rows[0];
    const changes: ChangeHistoryChange[] = [];
    let changeIdCounter = 1;

    rows.forEach((row) => {
      const fieldName = getCreativeFieldNameByPath(row.fieldPath);
      const hasValues = row.previousValue || row.newValue;

      if (!hasValues) {
        const arrayAdded = parseArrayField(row.arrayAdded);
        const arrayRemoved = parseArrayField(row.arrayRemoved);

        if (arrayAdded.length > 0) {
          changes.push({
            id: `cr-${rowIdCounter}-${changeIdCounter++}`,
            action: "added",
            fieldName,
            fieldPath: row.fieldPath,
            newValue: JSON.stringify(arrayAdded),
            entityType: "Creative",
            entityId: row.creativeId || MISSING_ID,
            arrayLength: arrayAdded.length,
            oldSnapshotId: row.oldSnapshotId,
            newSnapshotId: row.newSnapshotId,
          });
        }

        if (arrayRemoved.length > 0) {
          changes.push({
            id: `cr-${rowIdCounter}-${changeIdCounter++}`,
            action: "removed",
            fieldName,
            fieldPath: row.fieldPath,
            newValue: JSON.stringify(arrayRemoved),
            entityType: "Creative",
            entityId: row.creativeId || MISSING_ID,
            arrayLength: arrayRemoved.length,
            oldSnapshotId: row.oldSnapshotId,
            newSnapshotId: row.newSnapshotId,
          });
        }
      } else {
        changes.push({
          id: `cr-${rowIdCounter}-${changeIdCounter++}`,
          action: mapChangeType(row.changeType),
          fieldName,
          fieldPath: row.fieldPath,
          oldValue: row.previousValue || undefined,
          newValue: row.newValue || undefined,
          entityType: "Creative",
          entityId: row.creativeId || MISSING_ID,
          oldSnapshotId: row.oldSnapshotId,
          newSnapshotId: row.newSnapshotId,
          details: getCreativeChangeDetails(
            row.fieldPath,
            row.previousValue,
            row.newValue
          ),
        });
      }
    });

    if (changes.length > 0) {
      historyRows.push({
        id: `cr-${rowIdCounter}`,
        timestamp: new Date(firstRow.datetime),
        changes,
        changedBy: firstRow.user,
      });

      rowIdCounter++;
    }
  });

  return historyRows.sort(
    (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
  );
};

const isDaypartingChange = (fieldPath: string): boolean => {
  return /dayparting/.test(fieldPath);
};

type FieldAccordionCountFormatter = (arrayLength: number) => string;

const formatDaypartingCount: FieldAccordionCountFormatter = (
  arrayLength: number
): string => {
  const hours = arrayLength / 2;
  if (hours === 24) {
    return "1 day";
  } else if (hours < 24) {
    return `${hours} hours`;
  } else {
    // hours > 24
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    if (remainingHours === 0) {
      return `${days} ${days === 1 ? "day" : "days"}`;
    } else {
      return `${days} ${days === 1 ? "day" : "days"} and ${remainingHours} ${
        remainingHours === 1 ? "hour" : "hours"
      }`;
    }
  }
};

const FIELD_ACCORDION_COUNT_FORMATTERS: Array<{
  pattern: RegExp;
  formatter: FieldAccordionCountFormatter;
  unit: string; // Unit label (empty string means no unit in header)
}> = [
  {
    pattern: /dayparting/,
    formatter: formatDaypartingCount,
    unit: "",
  },
];

export const getFieldAccordionCountFormatter = (
  fieldPath: string
): { formatter: FieldAccordionCountFormatter; unit: string } | null => {
  for (const { pattern, formatter, unit } of FIELD_ACCORDION_COUNT_FORMATTERS) {
    if (pattern.test(fieldPath)) {
      return { formatter, unit };
    }
  }
  return null;
};

const getBaseDaypartingPath = (fieldPath: string): string => {
  const match = fieldPath.match(/^(.+\/dayparting)\/\d+\//);
  return match
    ? match[1]
    : fieldPath.replace(/\/dayparting\/\d+\/.*$/, "/dayparting");
};

export const processDayparting = (
  data: ChangeHistoryRow[]
): ChangeHistoryRow[] => {
  return data.map((row) => {
    const daypartingChangesByEntityAndAction = new Map<
      string,
      ChangeHistoryChange[]
    >();
    const nonDaypartingChanges: ChangeHistoryChange[] = [];

    row.changes.forEach((change) => {
      if (isDaypartingChange(change.fieldPath)) {
        const key = `${change.entityId}_${change.action}`;
        if (!daypartingChangesByEntityAndAction.has(key)) {
          daypartingChangesByEntityAndAction.set(key, []);
        }
        daypartingChangesByEntityAndAction.get(key)?.push(change);
      } else {
        nonDaypartingChanges.push(change);
      }
    });

    const processedDaypartingChanges: ChangeHistoryChange[] = [];
    let changeIdCounter = 1;

    daypartingChangesByEntityAndAction.forEach((changes) => {
      if (changes.length === 0) return;

      const firstChange = changes[0];
      const basePath = getBaseDaypartingPath(firstChange.fieldPath);
      const fieldName = getFieldNameByPath(basePath);

      const pathIndexToDayOfWeek = new Map<number, number>();
      changes.forEach((change) => {
        if (!change.fieldPath.endsWith("/dayOfWeek") || !change.newValue)
          return;
        const pathMatch = change.fieldPath.match(/\/dayparting\/(\d+)\//);
        if (!pathMatch) return;
        const pathIndex = parseInt(pathMatch[1], 10);
        const dayOfWeek = parseInt(change.newValue, 10);
        if (dayOfWeek >= 1 && dayOfWeek <= 7) {
          pathIndexToDayOfWeek.set(pathIndex, dayOfWeek);
        }
      });

      let totalArrayLength = 0;
      changes.forEach((change) => {
        totalArrayLength += change.arrayLength || 0;
      });
      const daypartingData: Array<{ dayIndex: number; slots: string[] }> = [];

      changes.forEach((change) => {
        const pathMatch = change.fieldPath.match(/\/dayparting\/(\d+)\//);
        if (!pathMatch || !change.newValue) return;
        const pathIndex = parseInt(pathMatch[1], 10);
        try {
          const parsed = JSON.parse(change.newValue);
          if (Array.isArray(parsed)) {
            const dayOfWeek = pathIndexToDayOfWeek.get(pathIndex);
            const dayIndex =
              dayOfWeek && dayOfWeek >= 1 && dayOfWeek <= 7
                ? dayOfWeek - 1
                : pathIndex;
            daypartingData.push({
              dayIndex,
              slots: parsed,
            });
          }
        } catch {
          // Skip if parsing fails
        }
      });

      if (daypartingData.length > 0) {
        daypartingData.sort((a, b) => a.dayIndex - b.dayIndex);

        const combinedValue = JSON.stringify(
          daypartingData.map((d) => ({
            dayIndex: d.dayIndex,
            slots: d.slots,
          }))
        );

        processedDaypartingChanges.push({
          id: `${row.id}-${changeIdCounter++}`,
          action: firstChange.action,
          fieldName,
          fieldPath: basePath,
          newValue: combinedValue,
          entityType: firstChange.entityType,
          entityId: firstChange.entityId,
          arrayLength: totalArrayLength,
        });
      }
    });

    const allChanges = [...nonDaypartingChanges, ...processedDaypartingChanges];

    return {
      ...row,
      changes: allChanges,
    };
  });
};
